import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  DbExplorerStateAndActions,
  useDbExplorerStore,
} from "../../stores/dbExplorerStore";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  SortAsc,
  SortDesc,
  X,
} from "lucide-react";
import React, { useMemo, useEffect, useRef } from "react";
import { useShallow } from "zustand/react/shallow";

// Inline date/time input replacing the DatePicker/DateTimePicker/TimePicker components
const FilterInput = React.memo(
  ({
    column,
    operator,
    value,
    onValueChange,
    getDataTypeCategory,
  }: {
    column: { column_name: string; data_type: string };
    operator: string;
    value: string;
    onValueChange: (value: string) => void;
    getDataTypeCategory: (dataType: string) => string;
  }) => {
    const category = getDataTypeCategory(column.data_type);

    if (["is_null", "not_null"].includes(operator)) {
      return null;
    }

    if (category === "boolean") {
      return (
        <Select value={value} onValueChange={onValueChange}>
          <SelectTrigger className="w-full h-7 text-xs">
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true" className="text-xs">True</SelectItem>
            <SelectItem value="false" className="text-xs">False</SelectItem>
          </SelectContent>
        </Select>
      );
    }

    if (category === "number") {
      return (
        <Input
          type="number"
          placeholder="Enter number..."
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          className="h-7 text-xs"
        />
      );
    }

    if (category === "datetime") {
      const dataType = column.data_type.toLowerCase();

      if (dataType.includes("timestamp")) {
        return (
          <Input
            type="datetime-local"
            value={value ? value.slice(0, 16) : ""}
            onChange={(e) => {
              const val = e.target.value;
              onValueChange(val ? new Date(val).toISOString() : "");
            }}
            className="h-7 text-xs"
          />
        );
      } else if (dataType === "time" || dataType.startsWith("time(")) {
        return (
          <Input
            type="time"
            value={value}
            onChange={(e) => onValueChange(e.target.value)}
            className="h-7 text-xs"
          />
        );
      } else {
        return (
          <Input
            type="date"
            value={value ? value.slice(0, 10) : ""}
            onChange={(e) => onValueChange(e.target.value)}
            className="h-7 text-xs"
          />
        );
      }
    }

    return (
      <Input
        placeholder="Filter value..."
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        className="h-7 text-xs"
      />
    );
  },
);

FilterInput.displayName = "FilterInput";

const FilterSection = React.memo(
  ({
    title,
    children,
    count = 0,
    isOpen,
    onToggle,
  }: {
    title: string;
    children: React.ReactNode;
    count?: number;
    isOpen: boolean;
    onToggle: () => void;
  }) => (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="flex h-8 w-full justify-between px-2 py-1.5 hover:bg-slate-50 rounded-sm"
        >
          <div className="flex items-center gap-2">
            <span className="font-medium text-xs text-slate-700">{title}</span>
            {count > 0 && (
              <Badge
                variant="outline"
                className="text-xs h-4 px-1 border-slate-300 bg-slate-100 text-slate-700"
              >
                {count}
              </Badge>
            )}
          </div>
          {isOpen ? (
            <ChevronUp className="h-3.5 w-3.5 text-slate-400" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
          )}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-2 pb-2 pt-1">
        <div className="space-y-2">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  ),
);

FilterSection.displayName = "FilterSection";

export function FilterControls() {
  const {
    appliedFilters,
    pendingFilters,
    tableData,
    isFiltersPanelOpen,
    openSections,
    setPendingFilters,
    applyFilters,
    clearFilters,
    setAppliedFilters,
    handleSort,
    getDataTypeCategory,
    getFilterOperators,
    setIsFiltersPanelOpen,
    toggleSection,
  } = useDbExplorerStore(
    useShallow((state: DbExplorerStateAndActions) => ({
      appliedFilters: state.appliedFilters,
      pendingFilters: state.pendingFilters,
      tableData: state.tableData,
      isFiltersPanelOpen: state.isFiltersPanelOpen,
      openSections: state.openSections,
      setPendingFilters: state.setPendingFilters,
      applyFilters: state.applyFilters,
      clearFilters: state.clearFilters,
      setAppliedFilters: state.setAppliedFilters,
      handleSort: state.handleSort,
      getDataTypeCategory: state.getDataTypeCategory,
      getFilterOperators: state.getFilterOperators,
      setIsFiltersPanelOpen: state.setIsFiltersPanelOpen,
      toggleSection: state.toggleSection,
    })),
  );

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const handleSearchChange = (value: string) => {
    setPendingFilters({ search: value });
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      applyFilters();
    }, 500);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      applyFilters();
    }
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (appliedFilters.search) count++;
    if (appliedFilters.filters?.length > 0) count += appliedFilters.filters.length;
    if (appliedFilters.sortColumn) count++;
    return count;
  }, [appliedFilters]);

  const hasUnappliedChanges = useMemo(() => {
    const searchChanged = pendingFilters.search !== appliedFilters.search;
    const filtersChanged =
      JSON.stringify(pendingFilters.filters || []) !==
      JSON.stringify(appliedFilters.filters || []);
    const hasInlineSelection = Boolean(pendingFilters.filterColumn);
    return searchChanged || filtersChanged || hasInlineSelection;
  }, [pendingFilters, appliedFilters]);

  const filterOperators = useMemo(() => {
    if (!pendingFilters.filterColumn || !tableData?.columns) return [];
    const column = tableData.columns.find(
      (col) => col.column_name === pendingFilters.filterColumn,
    );
    return column ? getFilterOperators(column.data_type) : [];
  }, [pendingFilters.filterColumn, tableData?.columns, getFilterOperators]);

  const canAddFilter = useMemo(() => {
    if (!pendingFilters.filterColumn) return false;
    if (["is_null", "not_null"].includes(pendingFilters.filterOperator)) return true;
    return pendingFilters.filterValue !== "";
  }, [pendingFilters.filterColumn, pendingFilters.filterOperator, pendingFilters.filterValue]);

  const handleAddFilter = () => {
    if (!canAddFilter) return;
    const newFilter = {
      column: pendingFilters.filterColumn,
      operator: pendingFilters.filterOperator,
      value: pendingFilters.filterValue,
    };
    setPendingFilters({
      filters: [...(pendingFilters.filters || []), newFilter],
      filterColumn: "",
      filterValue: "",
      filterOperator: "contains",
    });
  };

  const handleRemovePendingFilter = (index: number) => {
    const newFilters = [...(pendingFilters.filters || [])];
    newFilters.splice(index, 1);
    setPendingFilters({ filters: newFilters });
  };

  const handleRemoveAppliedFilter = (index: number) => {
    const newFilters = [...(appliedFilters.filters || [])];
    newFilters.splice(index, 1);
    setAppliedFilters({ filters: newFilters });
    setPendingFilters({ filters: newFilters });
    applyFilters();
  };

  const handleColumnChange = (columnName: string) => {
    const column = tableData?.columns.find((c) => c.column_name === columnName);
    if (!column) return;
    const operators = getFilterOperators(column.data_type);
    const defaultOperator = operators[0]?.value || "contains";
    setPendingFilters({ filterColumn: columnName, filterValue: "", filterOperator: defaultOperator });
  };

  const handleOperatorChange = (operator: string) => {
    setPendingFilters({
      filterOperator: operator,
      filterValue: ["is_null", "not_null"].includes(operator) ? "" : pendingFilters.filterValue,
    });
  };

  const handleSortColumnChange = (value: string) => {
    if (value === "none") {
      setAppliedFilters({ sortColumn: "", sortDirection: "asc" });
      applyFilters();
    } else {
      handleSort(value);
    }
  };

  const handleSortDirectionChange = (direction: string) => {
    setAppliedFilters({ sortDirection: direction as "asc" | "desc" });
    applyFilters();
  };

  if (!tableData || !isFiltersPanelOpen) return null;

  const selectedColumn = tableData.columns.find(
    (col) => col.column_name === pendingFilters.filterColumn,
  );

  return (
    <div className="bg-white h-full flex flex-col">
      <div className="flex-shrink-0 border-b border-slate-200 px-3 py-2.5">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsFiltersPanelOpen(false)}
            className="text-slate-600 hover:text-slate-700 h-7 px-2 -ml-2"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Hide Filters
            {activeFiltersCount > 0 && (
              <Badge
                variant="secondary"
                className="ml-2 h-5 px-1.5 text-xs bg-slate-200 text-slate-700"
              >
                {activeFiltersCount}
              </Badge>
            )}
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
        <FilterSection
          title="Global Search"
          sectionKey="search"
          count={appliedFilters.search ? 1 : 0}
          isOpen={openSections.search}
          onToggle={() => toggleSection("search")}
        >
          <div>
            <Label className="text-[10px] text-slate-600 mb-1 block">
              Search across all text columns
            </Label>
            <Input
              placeholder="Search..."
              value={pendingFilters.search}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="h-7 text-xs"
            />
          </div>
        </FilterSection>

        <FilterSection
          title="Sort Options"
          sectionKey="sort"
          count={appliedFilters.sortColumn ? 1 : 0}
          isOpen={openSections.sort}
          onToggle={() => toggleSection("sort")}
        >
          <div className="space-y-2">
            <div>
              <Label className="text-[10px] text-slate-600 mb-1 block">Sort Column</Label>
              <Select
                value={appliedFilters.sortColumn || "none"}
                onValueChange={handleSortColumnChange}
              >
                <SelectTrigger className="w-full h-7 text-xs">
                  <SelectValue placeholder="Select column" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" className="text-xs">No sorting</SelectItem>
                  {tableData.columns.map((column) => (
                    <SelectItem
                      key={column.column_name}
                      value={column.column_name}
                      className="text-xs"
                    >
                      {column.column_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {appliedFilters.sortColumn && (
              <div>
                <Label className="text-[10px] text-slate-600 mb-1 block">Direction</Label>
                <Select
                  value={appliedFilters.sortDirection}
                  onValueChange={handleSortDirectionChange}
                >
                  <SelectTrigger className="w-full h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asc" className="text-xs">
                      <div className="flex items-center space-x-2">
                        <SortAsc className="h-3 w-3" />
                        <span>Ascending</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="desc" className="text-xs">
                      <div className="flex items-center space-x-2">
                        <SortDesc className="h-3 w-3" />
                        <span>Descending</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </FilterSection>

        <FilterSection
          title="Column Filters"
          sectionKey="filters"
          count={appliedFilters.filters?.length || 0}
          isOpen={openSections.filters}
          onToggle={() => toggleSection("filters")}
        >
          <div className="space-y-2">
            <div>
              <Label className="text-[10px] text-slate-600 mb-1 block">Column</Label>
              <Select
                value={pendingFilters.filterColumn}
                onValueChange={handleColumnChange}
              >
                <SelectTrigger className="w-full h-7 text-xs">
                  <SelectValue placeholder="Select column" />
                </SelectTrigger>
                <SelectContent>
                  {tableData.columns.map((column) => (
                    <SelectItem
                      key={column.column_name}
                      value={column.column_name}
                      className="text-xs"
                    >
                      {column.column_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {pendingFilters.filterColumn && (
              <>
                <div>
                  <Label className="text-[10px] text-slate-600 mb-1 block">Operator</Label>
                  <Select
                    value={pendingFilters.filterOperator}
                    onValueChange={handleOperatorChange}
                  >
                    <SelectTrigger className="w-full h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {filterOperators.map((op) => (
                        <SelectItem key={op.value} value={op.value} className="text-xs">
                          {op.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedColumn && (
                  <div>
                    <Label className="text-[10px] text-slate-600 mb-1 block">Value</Label>
                    <FilterInput
                      column={selectedColumn}
                      operator={pendingFilters.filterOperator}
                      value={pendingFilters.filterValue}
                      onValueChange={(value) => setPendingFilters({ filterValue: value })}
                      getDataTypeCategory={getDataTypeCategory}
                    />
                  </div>
                )}

                <Button
                  size="sm"
                  onClick={handleAddFilter}
                  disabled={!canAddFilter}
                  className="w-full h-7 text-xs bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300"
                >
                  Add filter
                </Button>
              </>
            )}

            {pendingFilters.filters && pendingFilters.filters.length > 0 && (
              <div className="space-y-1 pt-1">
                <Label className="text-[10px] text-slate-600">Pending Filters</Label>
                {pendingFilters.filters.map((f, idx) => (
                  <Badge
                    key={`pending-${f.column}-${idx}`}
                    variant="outline"
                    className="flex items-center justify-between w-full text-xs py-1.5 px-2"
                  >
                    <span className="truncate">
                      {f.column} {f.operator}
                      {!["is_null", "not_null"].includes(f.operator) ? ` "${f.value}"` : ""}
                    </span>
                    <X
                      className="h-3 w-3 cursor-pointer ml-1 flex-shrink-0 hover:text-red-600"
                      onClick={() => handleRemovePendingFilter(idx)}
                    />
                  </Badge>
                ))}
              </div>
            )}

            {appliedFilters.filters && appliedFilters.filters.length > 0 && (
              <div className="space-y-1 pt-1">
                <Label className="text-[10px] text-slate-600">Active Filters</Label>
                {appliedFilters.filters.map((f, idx) => (
                  <Badge
                    key={`applied-${f.column}-${idx}`}
                    variant="default"
                    className="flex items-center justify-between w-full text-xs bg-blue-600 py-1.5 px-2"
                  >
                    <span className="truncate text-white">
                      {f.column} {f.operator}
                      {!["is_null", "not_null"].includes(f.operator) ? ` "${f.value}"` : ""}
                    </span>
                    <X
                      className="h-3 w-3 cursor-pointer ml-1 flex-shrink-0 hover:text-red-200"
                      onClick={() => handleRemoveAppliedFilter(idx)}
                    />
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </FilterSection>
      </div>

      <div className="flex-shrink-0 bg-white border-t border-slate-200 px-3 py-2.5 space-y-2">
        <Button
          onClick={applyFilters}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white disabled:bg-slate-300 disabled:text-slate-500 h-8 text-xs"
          size="sm"
          disabled={!hasUnappliedChanges}
        >
          <Check className="h-3.5 w-3.5 mr-1.5" />
          Apply Filters
        </Button>
        {activeFiltersCount > 0 && (
          <Button
            variant="ghost"
            onClick={clearFilters}
            className="w-full text-slate-600 hover:text-slate-900 hover:bg-slate-100 h-8 text-xs"
            size="sm"
          >
            Reset all {activeFiltersCount}
          </Button>
        )}
      </div>
    </div>
  );
}
